import type { PortfolioBookKindV2 } from "@tradvera/contracts/portfolio-v2";

export const MY_PORTFOLIO_NEXT_SCHEMA_VERSION = "tradvera.my-portfolio-next.v1" as const;
export const MY_PORTFOLIO_NEXT_FORMULA_VERSION = "my-portfolio-formulas.v1" as const;

export type MyPortfolioMetricState = "AVAILABLE" | "KNOWN_ZERO" | "UNKNOWN" | "STALE" | "CONFLICT";
export type MyPortfolioSampleState = "INSUFFICIENT_DATA" | "PROVISIONAL_TREND" | "RELATIVELY_STABLE_TREND";
export type MyPortfolioBehaviorSignalId = "FEAR" | "GREED" | "OVERCONFIDENCE" | "HESITATION" | "REVENGE";

export interface MyPortfolioSourcedMetric<T> {
  value: T | null;
  state: MyPortfolioMetricState;
  sourceIds: string[];
  evidenceIds: string[];
  asOf: string | null;
  knownAt: string | null;
  formulaVersion: typeof MY_PORTFOLIO_NEXT_FORMULA_VERSION;
  reasonCodes: string[];
}

export interface MyPortfolioSourceFileReceipt {
  fileId: string;
  originalName: string;
  sha256: string;
  role: "POSITIONS" | "CASH" | "ORDER_HISTORY" | "OPEN_ORDERS" | "ACTIVITY_LOG" | "OTHER";
  recordCount: number;
  state: "ACCEPTED" | "REVIEW_REQUIRED" | "BLOCKED";
  reasonCodes: string[];
}

export interface MyPortfolioDataQualityReceipt {
  state: "READY" | "READY_WITH_WARNINGS" | "REVIEW_REQUIRED" | "BLOCKED";
  sourceFileCount: number;
  sourceRecordCount: number;
  recognizedRoleCount: number;
  requiredRoles: Array<{ role: "POSITIONS" | "CASH" | "ORDER_HISTORY"; present: boolean }>;
  unknownFeeRecordCount: number;
  duplicateExecutionCount: number;
  warningCodes: string[];
  blockingCodes: string[];
  files: MyPortfolioSourceFileReceipt[];
}

export interface MyPortfolioSummaryReadModel {
  currentPortfolioValue: MyPortfolioSourcedMetric<number>;
  accountBalance: MyPortfolioSourcedMetric<number>;
  totalPurchaseValue: MyPortfolioSourcedMetric<number>;
  totalMarketValue: MyPortfolioSourcedMetric<number>;
  realizedPnl: MyPortfolioSourcedMetric<number>;
  unrealizedPnl: MyPortfolioSourcedMetric<number>;
  totalPnl: MyPortfolioSourcedMetric<number>;
  positionsCount: MyPortfolioSourcedMetric<number>;
  closedTradesCount: MyPortfolioSourcedMetric<number>;
  winRatePct: MyPortfolioSourcedMetric<number>;
  profitFactor: MyPortfolioSourcedMetric<number>;
}

export interface MyPortfolioHoldingReadModel {
  holdingId: string;
  instrumentId: string;
  symbol: string;
  companyName: MyPortfolioSourcedMetric<string>;
  assetClass: MyPortfolioSourcedMetric<string>;
  quantity: MyPortfolioSourcedMetric<number>;
  averagePurchasePrice: MyPortfolioSourcedMetric<number>;
  currentPrice: MyPortfolioSourcedMetric<number>;
  totalPurchaseValue: MyPortfolioSourcedMetric<number>;
  currentValue: MyPortfolioSourcedMetric<number>;
  unrealizedPnl: MyPortfolioSourcedMetric<number>;
  unrealizedPnlPct: MyPortfolioSourcedMetric<number>;
  firstBuyAt: MyPortfolioSourcedMetric<string>;
  lastBuyAt: MyPortfolioSourcedMetric<string>;
  holdingDurationMs: MyPortfolioSourcedMetric<number>;
  sector: MyPortfolioSourcedMetric<string>;
  positionQuality: MyPortfolioSourcedMetric<string>;
}

export interface MyPortfolioTradeFillReadModel {
  fillId: string;
  side: "BUY" | "SELL";
  quantity: number;
  price: number;
  occurredAt: string;
  sourceIds: string[];
  evidenceIds: string[];
}

export interface MyPortfolioClosedTradeReadModel {
  tradeId: string;
  instrumentId: string;
  symbol: string;
  companyName: MyPortfolioSourcedMetric<string>;
  direction: "LONG" | "SHORT";
  quantity: MyPortfolioSourcedMetric<number>;
  entryAt: MyPortfolioSourcedMetric<string>;
  exitAt: MyPortfolioSourcedMetric<string>;
  entryPrice: MyPortfolioSourcedMetric<number>;
  exitPrice: MyPortfolioSourcedMetric<number>;
  purchaseValue: MyPortfolioSourcedMetric<number>;
  saleValue: MyPortfolioSourcedMetric<number>;
  fees: MyPortfolioSourcedMetric<number>;
  realizedPnl: MyPortfolioSourcedMetric<number>;
  realizedPnlPct: MyPortfolioSourcedMetric<number>;
  durationMs: MyPortfolioSourcedMetric<number>;
  fills: MyPortfolioTradeFillReadModel[];
  tradeQuality: MyPortfolioSourcedMetric<string>;
  learningEvidence: string[];
}

export interface MyPortfolioTradeAggregateReadModel {
  instrumentId: string;
  symbol: string;
  companyName: MyPortfolioSourcedMetric<string>;
  closedTradesCount: MyPortfolioSourcedMetric<number>;
  totalQuantity: MyPortfolioSourcedMetric<number>;
  averageEntryPrice: MyPortfolioSourcedMetric<number>;
  lastExitPrice: MyPortfolioSourcedMetric<number>;
  totalPurchaseValue: MyPortfolioSourcedMetric<number>;
  totalSaleValue: MyPortfolioSourcedMetric<number>;
  realizedPnl: MyPortfolioSourcedMetric<number>;
  realizedPnlPct: MyPortfolioSourcedMetric<number>;
  firstEntryAt: MyPortfolioSourcedMetric<string>;
  lastExitAt: MyPortfolioSourcedMetric<string>;
  averageDurationMs: MyPortfolioSourcedMetric<number>;
  sector: MyPortfolioSourcedMetric<string>;
  aggregateQuality: MyPortfolioSourcedMetric<string>;
}

export interface MyPortfolioTradeHistoryReadModel {
  companies: MyPortfolioTradeAggregateReadModel[];
  trades: MyPortfolioClosedTradeReadModel[];
}

export interface MyPortfolioBehaviorSignalReadModel {
  signalId: MyPortfolioBehaviorSignalId;
  score: MyPortfolioSourcedMetric<number>;
  frequencyPct: MyPortfolioSourcedMetric<number>;
  severityPct: MyPortfolioSourcedMetric<number>;
  coveragePct: MyPortfolioSourcedMetric<number>;
  sampleState: MyPortfolioSampleState;
  eligibleDecisionCount: number;
  assessedDecisionCount: number;
  evidenceTradeIds: string[];
}

export interface MyPortfolioBehaviorReadModel {
  sampleSize: number;
  sampleState: MyPortfolioSampleState;
  wins: MyPortfolioSourcedMetric<number>;
  losses: MyPortfolioSourcedMetric<number>;
  winRatePct: MyPortfolioSourcedMetric<number>;
  averageWin: MyPortfolioSourcedMetric<number>;
  averageLoss: MyPortfolioSourcedMetric<number>;
  bestTradeId: string | null;
  worstTradeId: string | null;
  averageDurationMs: MyPortfolioSourcedMetric<number>;
  medianDurationMs: MyPortfolioSourcedMetric<number>;
  dominantHoldingStyle: MyPortfolioSourcedMetric<string>;
  mostTradedSymbol: MyPortfolioSourcedMetric<string>;
  signals: MyPortfolioBehaviorSignalReadModel[];
  weeklyProgress: {
    state: "IMPROVING" | "STABLE" | "DECLINING" | "INSUFFICIENT_DATA";
    delta: MyPortfolioSourcedMetric<number>;
    contributingTradeIds: string[];
  };
}

export interface MyPortfolioHermesReadModel {
  state: "NOT_REQUESTED" | "READY" | "REJECTED_UNSUPPORTED_CLAIM";
  narrative: string | null;
  factIds: string[];
  ruleIds: string[];
  evidenceIds: string[];
}

export interface MyPortfolioNextReadModel {
  schemaVersion: typeof MY_PORTFOLIO_NEXT_SCHEMA_VERSION;
  calculatedAt: string;
  identity: {
    ownerId: string;
    bookId: string;
    bookKind: PortfolioBookKindV2;
    currency: string;
  };
  freshness: { asOf: string | null; knownAt: string | null; state: "CURRENT" | "UNKNOWN" | "STALE" };
  dataQuality: MyPortfolioDataQualityReceipt;
  summary: MyPortfolioSummaryReadModel;
  holdings: MyPortfolioHoldingReadModel[];
  tradeHistory: MyPortfolioTradeHistoryReadModel;
  behavior: MyPortfolioBehaviorReadModel;
  importState: {
    state: "EMPTY" | "REVIEW_REQUIRED" | "READY" | "BLOCKED";
    bundleFingerprint: string | null;
    sourceFileIds: string[];
  };
  hermes: MyPortfolioHermesReadModel;
}
